/**
 * Unified searchable navigation catalog for Module Launcher (Alt+.) and Command Palette (Ctrl+K).
 * Derived from firm-navigation-catalog + workspace registry.
 */
import {
  NAV_CATALOG_SLOTS,
  OVERFLOW_CATEGORIES,
  type NavCatalogSlot,
} from "@/lib/firm-navigation-catalog";
import { WORKSPACES, type WorkspaceSubItem } from "@/lib/workspace-registry";
import { resolveModuleKeyForPath } from "@/lib/module-guard";
import { useModuleStore } from "@/lib/module-store";
import { useBusinessRules } from "@/lib/business-rules-store";

export type NavigationLauncherItem = {
  id: string;
  label: string;
  description?: string;
  category: string;
  to: string;
  search?: Record<string, unknown>;
  params?: Record<string, string>;
  keywords: string[];
};

function slotToLauncher(slot: NavCatalogSlot, category: string): NavigationLauncherItem {
  return {
    id: `slot_${slot.id}`,
    label: slot.word,
    description: `Primary workspace — ${slot.word}`,
    category,
    to: slot.to,
    search: slot.search,
    keywords: [slot.id, slot.word, slot.to, category].map((s) => s.toLowerCase()),
  };
}

function workspaceItemToLauncher(
  workspaceTitle: string,
  item: WorkspaceSubItem,
): NavigationLauncherItem {
  return {
    id: `ws_${item.id}`,
    label: item.title,
    description: item.description,
    category: workspaceTitle,
    to: item.to,
    search: item.search,
    params: item.params,
    keywords: [
      item.id,
      item.title,
      item.description ?? "",
      workspaceTitle,
      item.to,
    ]
      .join(" ")
      .toLowerCase()
      .split(/\s+/)
      .filter(Boolean),
  };
}

export function buildNavigationLauncherCatalog(): NavigationLauncherItem[] {
  const seen = new Set<string>();
  const items: NavigationLauncherItem[] = [];

  const push = (item: NavigationLauncherItem) => {
    const key = `${item.to}::${JSON.stringify(item.search ?? {})}::${JSON.stringify(item.params ?? {})}`;
    if (seen.has(key)) return;
    seen.add(key);
    items.push(item);
  };

  for (const slotId of ["home", "sell", "stock", "make", "money", "reports", "admin"] as const) {
    const slot = NAV_CATALOG_SLOTS[slotId];
    if (slot) push(slotToLauncher(slot, "Primary"));
  }

  for (const cat of OVERFLOW_CATEGORIES) {
    for (const leaf of cat.items) {
      push({
        id: `overflow_${leaf.id}`,
        label: leaf.label,
        description: cat.label,
        category: cat.label,
        to: leaf.to,
        search: leaf.search,
        keywords: [leaf.label, cat.label, leaf.to, leaf.id].map((s) => s.toLowerCase()),
      });
    }
  }

  for (const ws of Object.values(WORKSPACES)) {
    for (const sub of ws.items) {
      push(workspaceItemToLauncher(ws.title, sub));
    }
  }

  return items;
}

export function filterLauncherCatalog(
  items: NavigationLauncherItem[],
  query: string,
): NavigationLauncherItem[] {
  const q = query.trim().toLowerCase();
  const rules = useBusinessRules.getState();
  const isModuleEnabled = useModuleStore.getState().isModuleEnabled;

  return items.filter((item) => {
    const moduleKey = resolveModuleKeyForPath(item.to);
    if (moduleKey && !isModuleEnabled(moduleKey)) return false;
    if (item.to === "/ai-center" && !rules.isEnabled("enable_ai_assistant")) return false;

    if (!q) return true;
    const haystack = [item.label, item.description ?? "", item.category, ...item.keywords]
      .join(" ")
      .toLowerCase();
    return haystack.includes(q);
  });
}
